import {
  ArrowUpOutlined,
  CheckOutlined,
  CopyOutlined,
  DislikeFilled,
  DislikeOutlined,
  LikeFilled,
  LikeOutlined,
  ReloadOutlined,
  RobotOutlined,
  StopOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { isToolUIPart, type UIMessage } from "ai";
import { Avatar, Button, Mentions, Spin, Tooltip, Typography } from "antd";
import { useCallback, useRef, useState, type KeyboardEvent } from "react";

import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from "@fitness-app/web-ui/components/ai-elements/conversation";
import { Message, MessageContent } from "@fitness-app/web-ui/components/ai-elements/message";

import { SalesAgentMessageParts } from "~/modules/Sales/SalesAgent/SalesAgentMessageParts";
import { copyTextToClipboard } from "~/modules/Sales/SalesAgent/messageUtils";
import { analyticsService } from "~/store/initializeStore";

import {
  isMentionSearch,
  mentionsInText,
  readMessageMentions,
  splitByMentions,
  useMentionOptions,
  type TrainerAssistantMention,
} from "./mentions";
import { assistantMarkdownComponents, HelpSources } from "./TrainerAssistantRichContent";
import { activeStepLabel, TrainerAssistantSteps } from "./TrainerAssistantSteps";
import { ThinkingIndicator } from "./ThinkingIndicator";
import { readMessageRating, type TrainerAssistantChat } from "./useTrainerAssistantChat";

const SUGGESTIONS = [
  "Którymi klientkami mam się zająć?",
  "Co nowego w aplikacji w ostatnim miesiącu?",
  "Podsumuj ostatni tydzień @",
];

const MentionChip = ({ mention }: { mention: TrainerAssistantMention }) => (
  <span className="inline-flex h-[22px] items-center gap-1 rounded-md bg-white pl-[3px] pr-1.5 align-middle text-[13px] font-medium leading-none text-blue-700 ring-1 ring-inset ring-blue-200">
    <Avatar
      size={16}
      className="shrink-0"
      src={mention.avatarUrl || undefined}
      icon={mention.type === "trainer" ? <TeamOutlined /> : <UserOutlined />}
    />
    {mention.name}
  </span>
);

// Wiadomość użytkownika z wzmiankami: tekst wprost (bez markdownu, jak ją wpisał),
// a każde `@Imię Nazwisko` jako chip z avatarem.
const UserMessageWithMentions = ({
  message,
  mentions,
}: {
  message: UIMessage;
  mentions: TrainerAssistantMention[];
}) => {
  const text = message.parts.map((part) => (part.type === "text" ? part.text : "")).join("");
  return (
    <p className="m-0 whitespace-pre-wrap break-words leading-6">
      {splitByMentions(text, mentions).map((part, index) =>
        typeof part === "string" ? part : <MentionChip key={index} mention={part} />,
      )}
    </p>
  );
};

const textOf = (message: UIMessage) =>
  message.parts
    .map((part) => (part.type === "text" ? part.text : ""))
    .join("\n\n")
    .trim();

const ActionButton = ({ label, icon, onClick }: { label: string; icon: React.ReactNode; onClick: () => void }) => (
  <Tooltip title={label} mouseEnterDelay={0.4}>
    <Button
      type="text"
      size="small"
      className="!h-7 !w-7 !text-gray-400 hover:!text-gray-700"
      icon={icon}
      onClick={onClick}
      aria-label={label}
    />
  </Tooltip>
);

// Akcje pod odpowiedzią (wzór: AI Elements MessageActions): przy ostatniej zawsze, przy
// starszych po najechaniu. „Ponów” tylko przy ostatniej - serwer ucina historię za pytaniem.
const AssistantActions = ({
  chat,
  message,
  isLast,
}: {
  chat: TrainerAssistantChat;
  message: UIMessage;
  isLast: boolean;
}) => {
  const [copied, setCopied] = useState(false);
  const rating = readMessageRating(message.metadata);
  const rate = (value: "up" | "down") => void chat.rateMessage(message, rating === value ? null : value);

  return (
    <div
      className={`not-typeset -ml-1.5 mt-1.5 flex items-center gap-0.5 transition-opacity ${isLast ? "" : "opacity-0 group-hover/message:opacity-100"}`}
    >
      <ActionButton
        label={copied ? "Skopiowano" : "Kopiuj"}
        icon={copied ? <CheckOutlined /> : <CopyOutlined />}
        onClick={() => {
          void copyTextToClipboard(textOf(message));
          analyticsService.track("trainer_assistant_copied", { sessionId: chat.activeSessionId });
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1500);
        }}
      />
      {isLast ? (
        <ActionButton
          label="Ponów odpowiedź"
          icon={<ReloadOutlined />}
          onClick={() => {
            analyticsService.track("trainer_assistant_regenerated", { sessionId: chat.activeSessionId });
            void chat.regenerate();
          }}
        />
      ) : null}
      <ActionButton
        label="Dobra odpowiedź"
        icon={rating === "up" ? <LikeFilled className="!text-gray-700" /> : <LikeOutlined />}
        onClick={() => rate("up")}
      />
      <ActionButton
        label="Słaba odpowiedź"
        icon={rating === "down" ? <DislikeFilled className="!text-gray-700" /> : <DislikeOutlined />}
        onClick={() => rate("down")}
      />
    </div>
  );
};

const ChatMessage = ({
  chat,
  message,
  isLastMessage,
  streaming,
}: {
  chat: TrainerAssistantChat;
  message: UIMessage;
  isLastMessage: boolean;
  streaming: boolean;
}) => {
  if (message.role === "user") {
    const mentions = readMessageMentions(message.metadata);
    if (mentions.length) return <UserMessageWithMentions message={message} mentions={mentions} />;
    return <SalesAgentMessageParts message={message} isLastMessage={isLastMessage} isStreaming={streaming} />;
  }

  return (
    <>
      <TrainerAssistantSteps parts={message.parts.filter(isToolUIPart)} streaming={streaming} />
      {/* Dopóki nie ma tekstu odpowiedzi, wskaźnik mówi, co się dzieje (trwający krok albo czasownik). */}
      {streaming && !textOf(message) ? (
        <ThinkingIndicator label={activeStepLabel(message.parts.filter(isToolUIPart))} />
      ) : null}
      <SalesAgentMessageParts
        message={message}
        isLastMessage={isLastMessage}
        isStreaming={streaming}
        markdownComponents={assistantMarkdownComponents}
        hideLoader
      />
      {streaming ? null : <HelpSources message={message} />}
      {!streaming && textOf(message) ? <AssistantActions chat={chat} message={message} isLast={isLastMessage} /> : null}
    </>
  );
};

// Mentions to goły textarea, więc tokenów nie da się ostylować w miejscu. Ta warstwa
// leży POD przezroczystym textarea i maluje tło pod każdym `@Imię Nazwisko`; jej tekst
// jest przezroczysty, a metryki (14 px / 22 px, padding 4 px 11 px wrappera antd) zgadzają się z textarea.
const MentionsHighlight = ({ text, mentions }: { text: string; mentions: TrainerAssistantMention[] }) =>
  mentions.length ? (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 overflow-hidden whitespace-pre-wrap break-words px-[11px] py-[4px] text-sm leading-[22px] text-transparent"
    >
      {splitByMentions(text, mentions).map((part, index) =>
        typeof part === "string" ? (
          part
        ) : (
          <span key={index} className="rounded bg-blue-100">
            @{part.name}
          </span>
        ),
      )}
    </div>
  ) : null;

const Composer = ({ chat, compact }: { chat: TrainerAssistantChat; compact: boolean }) => {
  const { draft: text, setDraft: setText, pickedMentions: picked } = chat;
  const [search, setSearch] = useState("");
  const [searching, setSearching] = useState(false);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const { options, loading } = useMentionOptions(search, searching);

  const submit = useCallback(() => {
    const sent = chat.send(text, mentionsInText(text, picked.current));
    if (sent) {
      setText("");
      picked.current.clear();
    }
  }, [chat, picked, setText, text]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) return;
      // Otwarta lista wzmianek: Enter wybiera osobę (obsługuje go Mentions), nie wysyła.
      if (popupRef.current?.querySelector(".ant-mentions-dropdown:not(.ant-mentions-dropdown-hidden)")) return;
      event.preventDefault();
      submit();
    },
    [submit],
  );

  return (
    <div className={compact ? "border-t border-gray-100 p-3" : "px-4 pb-4"}>
      <SuggestionSlot chat={chat} onPick={setText} />
      <div
        className={`mx-auto flex items-end gap-2 rounded-2xl border border-gray-200 bg-white px-3 py-2 shadow-[0_8px_30px_rgba(15,23,42,0.06)] ${compact ? "" : "max-w-3xl"}`}
      >
        <div className="relative min-w-0 flex-1">
          {/* antd stawia listę przy kursorze, więc przy długim tekście wystawała poza okno i była ucięta.
              Przypinamy ją do szerokości pola. */}
          <div
            ref={popupRef}
            className="relative [&_.ant-mentions-dropdown]:!left-0 [&_.ant-mentions-dropdown]:!right-0 [&_.ant-mentions-dropdown]:!w-auto [&_.ant-mentions-dropdown]:!min-w-0"
          />
          <MentionsHighlight text={text} mentions={mentionsInText(text, picked.current)} />
          <Mentions
            variant="borderless"
            autoSize={{ minRows: 1, maxRows: 6 }}
            className="relative bg-transparent [&_textarea]:!bg-transparent [&_textarea]:!px-0"
            value={text}
            onChange={setText}
            onKeyDown={handleKeyDown}
            onSearch={(value) => {
              setSearching(true);
              setSearch(value);
            }}
            onSelect={(option) => {
              const mention = options.find((candidate) => `${candidate.type}:${candidate.id}` === option.key);
              if (mention) picked.current.set(option.key as string, mention);
            }}
            // Pobieramy listę od wejścia w pole: podpowiedź z „@” wstawia znak bez klawisza,
            // a Mentions otwiera listę przy kolejnej literze tylko wtedy, gdy ma już opcje.
            onFocus={() => setSearching(true)}
            // Opcje filtruje zapytanie (imię, nazwisko, e-mail) - filtr antd po nazwie
            // odrzuciłby klientkę znalezioną po e-mailu.
            filterOption={false}
            // Domyślnie spacja kończy wyszukiwanie, więc nazwisko nigdy nie trafiało do zapytania.
            validateSearch={(search) => isMentionSearch(search, picked.current)}
            getPopupContainer={() => popupRef.current ?? document.body}
            placement="top"
            notFoundContent={loading ? <Spin size="small" /> : "Brak wyników"}
            placeholder="Zapytaj albo oznacz klientkę przez @"
            disabled={chat.busy}
            options={options.map((option) => ({
              key: `${option.type}:${option.id}`,
              value: option.name,
              label: (
                <span className="flex items-center gap-2">
                  <Avatar
                    size={20}
                    src={option.avatarUrl || undefined}
                    icon={option.type === "trainer" ? <TeamOutlined /> : <UserOutlined />}
                  />
                  <span className="flex min-w-0 flex-col leading-tight">
                    <span className="truncate">{option.name}</span>
                    <span className="truncate text-xs text-gray-400">{option.description}</span>
                  </span>
                </span>
              ),
            }))}
          />
        </div>
        {chat.busy ? (
          <Button shape="circle" icon={<StopOutlined />} onClick={() => void chat.stop()} aria-label="Zatrzymaj" />
        ) : (
          <Button
            type="primary"
            shape="circle"
            icon={<ArrowUpOutlined />}
            disabled={!text.trim()}
            onClick={submit}
            aria-label="Wyślij"
          />
        )}
      </div>
    </div>
  );
};

// Podpowiedzi tylko przy pustej rozmowie; kliknięcie wstawia tekst do kompozera.
const SuggestionSlot = ({ chat, onPick }: { chat: TrainerAssistantChat; onPick: (text: string) => void }) =>
  chat.messages.length ? null : (
    <div className="mb-2 flex flex-wrap gap-1.5">
      {SUGGESTIONS.map((suggestion) => (
        <button
          key={suggestion}
          type="button"
          className="rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs text-gray-600 hover:border-gray-300 hover:text-gray-900"
          onClick={() => onPick(suggestion)}
        >
          {suggestion}
        </button>
      ))}
    </div>
  );

export const TrainerAssistantConversation = ({ chat, compact }: { chat: TrainerAssistantChat; compact: boolean }) => (
  <div className="flex h-full min-h-0 flex-col" data-chat-density={compact ? "compact" : "comfortable"}>
    <Conversation>
      {/* Rytm jak w presecie czatu Typeset: wyraźna przerwa między turami, odpowiedź bez dymka. */}
      <ConversationContent className={compact ? "gap-6 px-4 py-4" : "mx-auto w-full max-w-[720px] gap-10 px-6 py-8"}>
        {chat.messages.length ? (
          chat.messages.map((message, index) => {
            const isLast = index === chat.messages.length - 1;
            return (
              <Message key={message.id} from={message.role} className="group/message">
                <MessageContent
                  className={
                    message.role === "user"
                      ? `rounded-3xl border-transparent bg-gray-100 px-4 py-2.5 shadow-none ${compact ? "max-w-[85%]" : "max-w-[65%]"}`
                      : "w-full max-w-none border-transparent bg-transparent px-0 py-0 shadow-none"
                  }
                >
                  <ChatMessage
                    chat={chat}
                    message={message}
                    isLastMessage={isLast}
                    streaming={chat.status === "streaming" && isLast}
                  />
                </MessageContent>
              </Message>
            );
          })
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center px-6 py-8 text-center">
            <Avatar size={44} className="mb-3 bg-gray-900" icon={<RobotOutlined />} />
            <Typography.Text strong>W czym mogę pomóc?</Typography.Text>
          </div>
        )}
        {chat.status === "submitted" ? <ThinkingIndicator /> : null}
        {chat.error ? (
          <div className="flex items-center gap-2 text-[13px] text-red-600">
            Nie udało się uzyskać odpowiedzi.
            <Button size="small" icon={<ReloadOutlined />} onClick={() => void chat.regenerate()}>
              Ponów
            </Button>
          </div>
        ) : null}
      </ConversationContent>
      <ConversationScrollButton />
    </Conversation>
    <Composer chat={chat} compact={compact} />
  </div>
);
